// ============================================================
// 💶 TokenPriceChart.jsx — Suivi du prix ENTQ en temps réel
// ============================================================

import React, { useEffect, useState, useCallback } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import web3Service from "../services/web3Service";

// Fonction utilitaire pour convertir µEUR → EUR
const microToEur = (micro) => Number(micro) / 1_000_000;

export default function TokenPriceChart() {
  const [priceData, setPriceData] = useState([]);
  const [currentPrice, setCurrentPrice] = useState(null);
  const [error, setError] = useState(null);

  // ✅ Définir fetchPrice dans le scope du composant (et non dans useEffect)
  const fetchPrice = useCallback(async (isMountedRef) => {
    try {
      setError(null);

      const oracle = await web3Service.getContract("EntropyOracle");
      const microPrice = await oracle.microEURPerENTQ();

      if (isMountedRef && !isMountedRef.current) return; // unmounted check

      const eurPrice = microPrice ? microToEur(microPrice.toString()) : Math.random() * 2;
      const timestamp = new Date().toLocaleTimeString();

      setCurrentPrice(eurPrice);
      setPriceData((prev) => [
        ...prev.slice(-19), // garde les 20 derniers points max
        { time: timestamp, price: eurPrice },
      ]);
    } catch (err) {
      if (isMountedRef && !isMountedRef.current) return; // silently ignore if unmounted
      console.error("❌ Erreur récupération prix :", err);
      setError(err.message);
    }
  }, []);

  // ⚙️ Exécuter au démarrage et toutes les 5 secondes
  useEffect(() => {
    const isMountedRef = { current: true };
    fetchPrice(isMountedRef);
    const interval = setInterval(() => fetchPrice(isMountedRef), 5000);
    return () => {
      isMountedRef.current = false;
      clearInterval(interval);
    };
  }, [fetchPrice]);

  return (
    <div className="p-6 text-white">
      <h1 className="text-2xl font-bold mb-4">💶 Cours en temps réel — ENTQ / EUR</h1>

      <button
        onClick={fetchPrice}
        className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-md mb-4"
      >
        🔄 Actualiser
      </button>

      {error ? (
        <p className="text-red-400">Erreur : {error}</p>
      ) : (
        <>
          <div className="mb-4">
            <p className="text-lg">
              Prix actuel :{" "}
              <span className="text-green-400 font-semibold">
                {currentPrice ? `${currentPrice.toFixed(6)} €` : "..."}
              </span>
            </p>
          </div>

          <div className="bg-gray-900 rounded-xl p-4 shadow-lg" style={{ minWidth: "100px", minHeight: "100px" }}>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={priceData}>
                <XAxis dataKey="time" stroke="#aaa" />
                <YAxis stroke="#aaa" />
                <Tooltip
                  contentStyle={{ backgroundColor: "#222", border: "none", color: "#fff" }}
                  labelStyle={{ color: "#aaa" }}
                  formatter={(value) => [`${value.toFixed(6)} €`, "Prix"]}
                />
                <Line type="monotone" dataKey="price" stroke="#00ff88" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
}
